// DEPLOY: /var/www/woody.wtf/frontend/src/chart/ChartEngine.js
// woody.wtf chart engine (no third-party charting library).
// Two stacked canvases: `base` (grid, candles, volume, axes) redraws when the view
// changes; `over` (crosshair + its labels) redraws on pointer moves only.
// A small `lens` canvas (phones) magnifies the area under the finger while drawing,
// dragging a handle or moving the long-press crosshair.
// The x axis counts candles, not dates: candle i sits at xOf(i), and slots past the
// last candle are blank space for drawing into the future.
// Layout top to bottom: price pane (+ volume at its foot), one pane per "own pane"
// indicator (resizable by dragging its top edge), date axis.
// Intraday (1m-4h, `intraday` = {barMin}): candles are New York minutes; the axis shows days and
// times, pre-market / after-hours are shaded, and drawings anchor to the 1-minute candles
// (point d = 'YYYY-MM-DDTHH:MMZ') the same way daily drawings anchor to daily candles.
import { readTheme, followTheme } from './theme';
import { INDICATORS } from './indicators';
import { attachGestures } from './gestures';

const AXIS_W = 64;      // right price axis width (css px)
const AXIS_H = 24;             // bottom date axis height
const VOL_FRAC = 0.18;         // share of the chart height given to the volume pane
const PAD_TOP = 10;
const FUTURE_BARS = 260;       // blank candle slots allowed after the last candle (~1 trading year)
const DEFAULT_BARS = 390;      // default view: ~18 months of daily candles
const MIN_DEFAULT_W = 4;      // default view never shrinks candles below this (phones show fewer months)
const BLANK_FRAC = 0.08;       // default blank space on the right, as a share of the plot width
const MAX_BAR_W = 80;
const PANE_H = 110;            // default height of an indicator pane
const PANE_MIN = 40;
const LENS = 120;              // magnifier size (css px)
const LENS_ZOOM = 2;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const NY_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hourCycle: 'h23',
  year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', weekday: 'short' });
// New York calendar parts of a timestamp: {y, mo, day, wd, min (minutes since midnight)}.
export function nyParts(t) {
  const p = {};
  for (const x of NY_FMT.formatToParts(t)) p[x.type] = x.value;
  return { y: +p.year, mo: +p.month, day: +p.day, wd: p.weekday, min: (+p.hour % 24) * 60 + +p.minute };
}
// Session of a New York minute: pre-market before 09:30, after-hours from 16:00.
export const nySession = min => (min < 570 ? 'pre' : min >= 960 ? 'post' : 'reg');
const hhmm = min => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

function niceStep(raw) {
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const f = raw / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
}

export function priceDp(step) { return clamp(-Math.floor(Math.log10(step)), 2, 6); }

export default class ChartEngine {
  // onHover(bar, idx) as the crosshair moves; onLayout([{key, top, h}]) when indicator panes move.
  // onView() after every redraw of the base layer (the date slider follows the view).
  constructor(host, { onHover, onLayout, onView } = {}) {
    this.host = host;
    this.onView = onView || (() => {});
    this.onHover = onHover || (() => {});
    this.onLayout = onLayout || (() => {});
    this.intraday = null;                    // {barMin} on 1m-4h timeframes (bars carry day/dd/mo/wd/min/sess)
    this.studies = [];                       // indicators: {key, id, visible, params, styles, fillOpacity, out}
    this.paneH = {};                         // study key -> pane height (px)
    this.base = document.createElement('canvas');
    this.over = document.createElement('canvas');
    for (const c of [this.base, this.over]) {
      Object.assign(c.style, { position: 'absolute', inset: '0', width: '100%', height: '100%' });
      host.appendChild(c);
    }
    this.over.style.touchAction = 'none';    // the chart owns every touch gesture
    this.lens = document.createElement('canvas');
    Object.assign(this.lens.style, { position: 'absolute', top: '6px', width: LENS + 'px', height: LENS + 'px', display: 'none',
      pointerEvents: 'none', borderRadius: '10px', border: '2px solid var(--border)', boxShadow: 'var(--shadow)' });
    host.appendChild(this.lens);
    this.loupe = null;                       // {x, y} point shown in the magnifier, or null
    this.bars = [];                          // [{t, o, h, l, c, v}]
    this.hasVol = false;
    this.barW = 8;                           // px per candle slot
    this.right = 0;                          // candle index (float) at the plot's right edge
    this.yMan = null;                        // {min, max} once the price scale is set by hand
    this.cross = null;                       // {x, y} crosshair position in css px
    this.showLast = true;                    // last-close dashed line + axis tag
    this.patterns = [];                      // auto chart patterns (chartPatterns.detectPatterns output)
    this.analystMarks = [];                  // [{d, target, norm, firm, rating}] analyst rating dates
    this.analystStyle = { dotSize: 3, dotAlpha: 0.85, line: true, lineStyle: 'dotted', lineWidth: 1, lineAlpha: 0.35 };   // set from AnalystMenu prefs
    this.auditMarks = [];                    // numbered, ephemeral alert rays (not saved)
    this.theme = readTheme(host);
    this.frame = 0;
    this.w = this.h = 0;
    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(host);
    this.detach = attachGestures(this.over, this);
    this.unfollow = followTheme(this, host, () => this.draw());
    this.resize();
  }

  destroy() {
    this.ro.disconnect();
    this.detach();
    this.unfollow();
    cancelAnimationFrame(this.frame);
    this.base.remove(); this.over.remove(); this.lens.remove();
  }

  // bars = candles shown (any timeframe); daily = the daily candles they were built from
  // ({t, d: 'YYYY-MM-DD', f: adj_factor}), which drawings are anchored to.
  setData(bars, hasVol, daily = bars) {
    this.bars = bars;
    this.hasVol = hasVol;
    this.daily = daily;
    // Shown-candle index for every daily candle (both lists are in time order).
    this.dispOfDaily = new Int32Array(daily.length);
    for (let i = 0, j = 0; i < daily.length; i++) {
      while (j + 1 < bars.length && bars[j + 1].t <= daily[i].t) j++;
      this.dispOfDaily[i] = j;
    }
    // Candles per month from the data's own spacing (daily ~21, weekly ~4.3, monthly 1),
    // used to space the date labels on any timeframe.
    const n = bars.length, span = n > 1 ? bars[n - 1].t - bars[0].t : 0;
    this.barsPerMonth = span > 0 ? (n - 1) * 30.44 * 86400000 / span : 21;
    this.computeStudies();
    this.reset();
  }

  // Same as setData without moving the view: today's live candle changed (Chart2 live polling).
  updateData(bars, hasVol, daily = bars) {
    const keep = { right: this.right, barW: this.barW, yMan: this.yMan };
    this.setData(bars, hasVol, daily);
    Object.assign(this, keep);
    this.draw();
  }

  // ── Indicators ────────────────────────────────────────────────────────────
  setStudies(list) { this.studies = list; this.computeStudies(); this.draw(); }

  // Indicators use every candle shown, today's purple one included - but not a pre-market candle.
  computeStudies() {
    const n = this.bars.length, src = n && this.bars[n - 1].live === 'pre' ? this.bars.slice(0, -1) : this.bars;
    for (const s of this.studies) s.out = src.length ? INDICATORS[s.id].calc(src, s.params) : {};
  }

  overlays() { return this.studies.filter(s => s.visible && INDICATORS[s.id].pane === 'price'); }

  // Indicator panes, top to bottom, squeezed if they would leave the price pane under 40%.
  panes() {
    const own = this.studies.filter(s => s.visible && INDICATORS[s.id].pane === 'own');
    const avail = this.h - AXIS_H, hs = own.map(s => this.paneH[s.key] || PANE_H);
    const total = hs.reduce((a, b) => a + b, 0), k = total > avail * 0.6 ? avail * 0.6 / total : 1;
    let top = avail - total * k;
    return own.map((s, i) => { const p = { s, top, h: hs[i] * k }; top += p.h; return p; });
  }

  get mainBottom() { const p = this.panes(); return p.length ? p[0].top : this.h - AXIS_H; }

  // Top edge of an indicator pane under y (drag it to resize), or -1.
  dividerAt(y, touch) {
    const tol = touch ? 10 : 5;
    return this.panes().findIndex(p => Math.abs(y - p.top) <= tol);
  }

  resizePane(i, dy) {
    const p = this.panes()[i];
    if (!p) return;
    this.paneH[p.s.key] = clamp((this.paneH[p.s.key] || PANE_H) - dy, PANE_MIN, (this.h - AXIS_H) * 0.6);
    this.draw();
  }

  // ── Geometry ──────────────────────────────────────────────────────────────
  get plotR() { return this.w - AXIS_W; }
  get volTop() { const mb = this.mainBottom; return this.hasVol ? mb * (1 - VOL_FRAC) : mb; }
  get priceBottom() { return this.volTop - (this.hasVol ? 6 : 0); }
  xOf(i) { return this.plotR - (this.right - i) * this.barW; }
  idxAt(x) { return this.right - (this.plotR - x) / this.barW; }
  // Fully zoomed out = whole history plus a little blank space.
  minBarW() { return Math.max(0.05, this.plotR / (this.bars.length * 1.05 + 5)); }

  visible() {
    const n = this.bars.length;
    return [Math.max(0, Math.floor(this.idxAt(0))), Math.min(n - 1, Math.ceil(this.right))];
  }

  // Price range of the price pane: hand-set, or fitted to the visible candles.
  yRange() {
    if (this.yMan) return this.yMan;
    const [i0, i1] = this.visible();
    let lo = Infinity, hi = -Infinity;
    for (let i = i0; i <= i1; i++) { const b = this.bars[i]; if (b.l < lo) lo = b.l; if (b.h > hi) hi = b.h; }
    if (!isFinite(lo)) return this._lastAuto || { min: 0, max: 1 };
    for (const s of this.overlays()) {          // keep on-price indicators in view too
      for (const a of Object.values(s.out || {})) {
        for (let i = i0; i <= i1; i++) { const v = a[i]; if (v < lo) lo = v; if (v > hi) hi = v; }
      }
    }
    for (const mark of this.intraday ? [] : this.auditMarks) {   // daily-dated: not on intraday
      const di = this.dailyIdx(mark.d);
      if (di >= 0 && this.daily[di].d === mark.d &&
          this.dispOfDaily[di] >= i0 && this.dispOfDaily[di] <= i1) {
        const p = mark.price * (this.daily[di].f || 1);
        if (Number.isFinite(p)) { lo = Math.min(lo, p); hi = Math.max(hi, p); }
      }
    }
    const pad = (hi - lo || hi || 1) * 0.06;
    return (this._lastAuto = { min: lo - pad, max: hi + pad });
  }

  yOf(p, r) { return PAD_TOP + (r.max - p) / (r.max - r.min) * (this.priceBottom - PAD_TOP); }
  priceAt(y, r) { return r.max - (y - PAD_TOP) / (this.priceBottom - PAD_TOP) * (r.max - r.min); }

  volumeRange() {
    const [i0, i1] = this.visible();
    let max = 0;
    for (let i = i0; i <= i1; i++) max = Math.max(max, this.bars[i].v || 0);
    return { min: 0, max: max || 1 };
  }
  volumePane() {
    return this.hasVol ? { s: { key: 'volume' }, top: this.volTop, h: this.mainBottom - this.volTop } : null;
  }
  paneFor(key) { return key === 'volume' ? this.volumePane() : this.panes().find(p => p.s.key === key) || null; }
  paneAt(y) {
    const vol = this.volumePane();
    if (vol && y >= vol.top && y < vol.top + vol.h) return vol;
    return this.panes().find(p => y > p.top + 1 && y < p.top + p.h) || null;
  }
  paneY(v, pane, r) {
    if (pane.s.key === 'volume') return this.mainBottom - v / r.max * (pane.h - 4);
    return pane.top + 4 + (r.max - v) / (r.max - r.min || 1) * (pane.h - 8);
  }
  paneValueAt(y, pane, r) {
    if (pane.s.key === 'volume') return (this.mainBottom - y) / (pane.h - 4) * r.max;
    return r.max - (y - pane.top - 4) / (pane.h - 8) * (r.max - r.min);
  }

  // ── Drawing anchors ───────────────────────────────────────────────────────
  // A drawing point is {d, off, p}: daily candle date, candles past it (blank future
  // area, counted in daily candles) and the as-traded price on that date. On screen
  // the price is split-adjusted with that day's adj_factor, like the candles.
  dailyIdx(d) {
    return this.baseIdxAt(d.length > 10 ? Date.parse(d) : Date.parse(d + 'T00:00:00Z'));
  }

  // Last base (daily / 1-minute) candle at or before time t.
  baseIdxAt(t) {
    const D = this.daily;
    if (!D?.length) return -1;
    let lo = 0, hi = D.length - 1;
    while (lo < hi) { const m = (lo + hi + 1) >> 1; if (D[m].t <= t) lo = m; else hi = m - 1; }
    return lo;
  }

  ptIndex(pt) {
    const di = this.dailyIdx(pt.d);
    if (di < 0) return 0;
    const ratio = this.daily.length > 1 ? (this.bars.length - 1) / (this.daily.length - 1) : 1;
    return this.dispOfDaily[di] + (pt.off || 0) * ratio;
  }

  ptFactor(pt) { const di = this.dailyIdx(pt.d); return (di >= 0 && this.daily[di].f) || 1; }

  ptToXY(pt, r, paneKey = null) {
    const pane = paneKey ? this.paneFor(paneKey) : null;
    if (paneKey && !pane) return null;
    const range = r || (pane ? this.paneRange(pane.s, this.visible()[0]) : this.yRange());
    return { x: this.xOf(this.ptIndex(pt)),
             y: pane ? this.paneY(pt.p, pane, range) : this.yOf(pt.p * this.ptFactor(pt), range) };
  }

  // Screen point -> anchor. The x always snaps to a candle slot; with `magnet` the price
  // snaps to that candle's nearest open/high/low/close when within 40px.
  xyToPt(x, y, magnet, paneKey = null) {
    const n = this.bars.length, D = this.daily, pane = paneKey ? this.paneFor(paneKey) : null;
    if (!n || !D?.length || (paneKey && !pane)) return null;
    const r = pane ? this.paneRange(pane.s, this.visible()[0]) : this.yRange();
    const idx = Math.round(this.idxAt(x));
    let price = pane ? this.paneValueAt(y, pane, r) : this.priceAt(y, r);
    if (!pane && magnet && idx >= 0 && idx < n) {
      const b = this.bars[idx];
      const best = [b.o, b.h, b.l, b.c].reduce((a, v) => (Math.abs(this.yOf(v, r) - y) < Math.abs(this.yOf(a, r) - y) ? v : a));
      if (Math.abs(this.yOf(best, r) - y) <= 40) price = best;
    }
    let di, off = 0;
    if (idx >= n) {
      di = D.length - 1;
      const ratio = n > 1 ? (D.length - 1) / (n - 1) : 1;
      off = Math.round((idx - (n - 1)) * ratio);
    } else {
      // First base candle of the shown candle's period.
      di = this.baseIdxAt(this.bars[Math.max(0, idx)].t);
    }
    const f = pane ? 1 : D[di].f || 1;
    return { d: D[di].d, off, p: price / f };
  }

  // ── View changes (called by gestures) ─────────────────────────────────────
  clampView() {
    const n = this.bars.length;
    this.barW = clamp(this.barW, this.minBarW(), MAX_BAR_W);
    this.right = clamp(this.right, Math.min(5, n - 1), n - 1 + FUTURE_BARS);
  }

  zoomAt(x, factor) {
    const idx = this.idxAt(x);
    this.barW = clamp(this.barW * factor, this.minBarW(), MAX_BAR_W);
    this.right = idx + (this.plotR - x) / this.barW;
    this.clampView();
    this.draw();
  }

  panBy(dx, dy = 0) {
    this.right -= dx / this.barW;
    if (this.yMan && dy) {
      const shift = dy * (this.yMan.max - this.yMan.min) / (this.priceBottom - PAD_TOP);
      this.yMan = { min: this.yMan.min + shift, max: this.yMan.max + shift };
    }
    this.clampView();
    this.draw();
  }

  // Stretch the price scale about its centre; factor > 1 zooms out.
  scaleY(factor, from) {
    const r = from || this.yRange();
    const mid = (r.max + r.min) / 2, half = (r.max - r.min) / 2 * factor;
    this.yMan = { min: mid - half, max: mid + half };
    this.draw();
  }

  reset() {
    const n = this.bars.length, pw = this.plotR;
    if (!n || pw <= 0) return this.draw();
    const show = Math.max(1, Math.min(n, DEFAULT_BARS, Math.floor(pw * (1 - BLANK_FRAC) / MIN_DEFAULT_W)));
    this.barW = clamp(pw * (1 - BLANK_FRAC) / show, this.minBarW(), MAX_BAR_W);
    this.right = n - 1 + (pw * BLANK_FRAC) / this.barW;
    this.yMan = null;
    this.clampView();
    this.draw();
  }

  // Show the alert candle with room to compare the candles that followed it.
  focusAudit(d) {
    if (!this.daily?.length || !this.bars.length) return;
    const di = this.dailyIdx(d);
    if (di < 0 || this.daily[di].d !== d) return;
    this.barW = clamp(this.plotR / 32, this.minBarW(), MAX_BAR_W);
    this.right = this.dispOfDaily[di] + 9;
    this.yMan = null;
    this.clampView();
    this.draw();
  }

  // Whole history in view, with a little blank space on the right.
  fitAll() {
    const n = this.bars.length;
    if (!n) return;
    this.barW = this.minBarW();
    this.right = n - 1 + (this.plotR * 0.02) / this.barW;
    this.yMan = null;
    this.clampView();
    this.draw();
  }

  setCross(pt) { this.cross = pt; this.drawOver(); this.drawLens(); }
  setLoupe(pt) { this.loupe = pt; this.drawLens(); }

  // ── Sizing / scheduling ───────────────────────────────────────────────────
  resize() {
    const r = this.host.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const first = !this.w;
    this.w = r.width; this.h = r.height;
    this.dpr = window.devicePixelRatio || 1;
    for (const c of [this.base, this.over]) {
      c.width = Math.round(r.width * this.dpr);
      c.height = Math.round(r.height * this.dpr);
    }
    if (first && this.bars.length) this.reset(); else { this.clampView(); this.draw(); }
  }

  draw() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => { this.drawBase(); this.drawOver(); this.drawLens(); this.onView(); });
  }

  ctx(c) {
    const g = c.getContext('2d');
    g.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    return g;
  }

  // Daily-family date axis: label every N months, N chosen so labels stay >= ~70px apart.
  drawDateAxis(g, i0, i1, plotR) {
    const per = [1, 3, 6, 12, 24, 60, 120].find(m => this.barW * this.barsPerMonth * m >= 56) || 240;
    let prevKey = null, lastX = -Infinity;
    g.textAlign = 'center'; g.beginPath();
    for (let i = Math.max(0, i0 - 1); i <= i1; i++) {
      const d = new Date(this.bars[i].t), key = d.getFullYear() * 12 + d.getMonth();
      if (prevKey !== null && key !== prevKey && key % per === 0) {
        const x = Math.round(this.xOf(i)) + 0.5;
        const half = g.measureText('0000').width / 2 + 2;   // keep labels whole at the edges
        if (x - lastX > 50 && x > half && x < plotR - half) {
          g.moveTo(x, PAD_TOP); g.lineTo(x, this.h - AXIS_H);
          g.fillText(d.getMonth() === 0 || per >= 12 ? String(d.getFullYear()) : MONTHS[d.getMonth()], x, this.h - AXIS_H / 2);
          lastX = x;
        }
      }
      prevKey = key;
    }
    g.stroke();
  }

  // Intraday axis: pre-market / after-hours shading, a line + date at each New York day,
  // and time labels inside days when there is room (every 15 min up to every 4 h).
  drawIntradayAxis(g, i0, i1, plotR) {
    const { barMin } = this.intraday, B = this.bars, half = this.barW / 2;
    g.save(); g.globalAlpha = 0.06; g.fillStyle = this.theme.text;
    for (let i = Math.max(0, i0); i <= i1; i++) {
      if (B[i].sess === 'reg') continue;
      let j = i;
      while (j + 1 <= i1 && B[j + 1].sess !== 'reg' && B[j + 1].day === B[i].day) j++;
      const x0 = Math.max(0, this.xOf(i) - half), x1 = Math.min(plotR, this.xOf(j) + half);
      if (x1 > x0) g.fillRect(x0, PAD_TOP, x1 - x0, this.h - AXIS_H - PAD_TOP);
      i = j;
    }
    g.restore();
    const step = [15, 30, 60, 120, 240].find(m => m >= barMin && this.barW * m / barMin >= 56);
    let lastX = -Infinity;
    const w = g.measureText('00 Mmm').width / 2 + 2;
    g.textAlign = 'center'; g.beginPath();
    for (let i = Math.max(1, i0); i <= i1; i++) {
      const b = B[i], newDay = b.day !== B[i - 1].day;
      if (!newDay && !(step && b.min % step === 0)) continue;
      const x = Math.round(this.xOf(i)) + 0.5;
      if (x - lastX <= 50 || x <= w || x >= plotR - w) continue;
      if (newDay) { g.moveTo(x, PAD_TOP); g.lineTo(x, this.h - AXIS_H); }
      g.fillStyle = newDay ? this.theme.text : this.theme.muted;
      g.fillText(newDay ? `${b.dd} ${MONTHS[b.mo - 1]}` : hhmm(b.min), x, this.h - AXIS_H / 2);
      lastX = x;
    }
    g.stroke();
    g.fillStyle = this.theme.muted;
  }

  // ── Base layer ────────────────────────────────────────────────────────────
  drawBase() {
    if (!this.w) return;
    const g = this.ctx(this.base), T = this.theme, n = this.bars.length;
    g.fillStyle = T.bg;
    g.fillRect(0, 0, this.w, this.h);
    if (!n) return;

    const r = this.yRange(), [i0, i1] = this.visible(), plotR = this.plotR;
    g.font = T.font;
    g.textBaseline = 'middle';

    // Price grid + labels
    const step = niceStep((r.max - r.min) / Math.max(2, (this.priceBottom - PAD_TOP) / 56));
    const dp = priceDp(step);
    g.strokeStyle = T.grid; g.fillStyle = T.muted; g.lineWidth = 1; g.textAlign = 'left';
    g.beginPath();
    for (let p = Math.ceil(r.min / step) * step; p <= r.max; p += step) {
      const y = Math.round(this.yOf(p, r)) + 0.5;
      if (y < PAD_TOP || y > this.priceBottom) continue;
      g.moveTo(0, y); g.lineTo(plotR, y);
      g.fillText(p.toFixed(dp), plotR + 6, y);
    }
    g.stroke();

    if (this.intraday) this.drawIntradayAxis(g, i0, i1, plotR);
    else this.drawDateAxis(g, i0, i1, plotR);

    // Volume pane
    if (this.hasVol) {
      let vmax = 0;
      for (let i = i0; i <= i1; i++) vmax = Math.max(vmax, this.bars[i].v || 0);
      const top = this.volTop, bot = this.mainBottom, vh = bot - top;
      g.strokeStyle = T.grid; g.beginPath();
      g.moveTo(0, Math.round(top) + 0.5); g.lineTo(plotR, Math.round(top) + 0.5); g.stroke();
      if (vmax > 0) {
        // Zoomed far out, several candles share a pixel: draw only the biggest
        // volume per pixel column so overlapping bars don't smear together.
        const cols = new Map(), bw = Math.max(1, this.barW * 0.7);
        for (let i = i0; i <= i1; i++) {
          const b = this.bars[i];
          if (!b.v) continue;
          const key = this.barW < 2 ? Math.floor(this.xOf(i)) : i;
          const cur = cols.get(key);
          if (!cur || b.v > cur.v) cols.set(key, { v: b.v, up: b.c >= b.o, x: this.xOf(i), live: b.live });
        }
        g.globalAlpha = 0.35;
        for (const up of [true, false]) {
          g.fillStyle = up ? T.up : T.down;
          for (const c of cols.values()) {
            if (c.up !== up || c.live) continue;
            const hgt = c.v / vmax * (vh - 4);
            g.fillRect(c.x - bw / 2, bot - hgt, bw, hgt);
          }
        }
        for (const c of cols.values()) {          // today's unconfirmed volume: live colours
          if (!c.live) continue;
          g.fillStyle = c.live === 'pre' ? T.candleLine : T.live;
          const hgt = c.v / vmax * (vh - 4);
          g.fillRect(c.x - bw / 2, bot - hgt, bw, hgt);
        }
        g.globalAlpha = 1;
      }
    }

    // Candles, drawn in device pixels so wick and body share one centre column on
    // any screen density. Wick and body width have the same parity, so both sit
    // exactly centred. Bodies: outline (theme/user wick colour, or the candle's own) + up/down fill or hollow.
    // Zoomed far out (< 3px per candle) a plain coloured high-low line per candle reads better.
    g.save();
    g.beginPath(); g.rect(0, 0, plotR, this.priceBottom + 1); g.clip();
    g.setTransform(1, 0, 0, 1, 0, 0);
    const dpr = this.dpr, lw = Math.max(1, Math.round(dpr));
    const Y = p => Math.round(this.yOf(p, r) * dpr);
    const X = i => Math.round(this.xOf(i) * dpr);
    if (this.barW < 3) {
      for (const up of [true, false]) {
        g.fillStyle = up ? T.up : T.down;
        for (let i = i0; i <= i1; i++) {
          const k = this.bars[i];
          if ((k.c >= k.o) !== up || k.live) continue;
          const yh = Y(k.h);
          g.fillRect(X(i) - (lw >> 1), yh, lw, Math.max(lw, Y(k.l) - yh));
        }
      }
      for (let i = i0; i <= i1; i++) {               // live candle: purple (pre-market: black)
        const k = this.bars[i];
        if (!k.live) continue;
        g.fillStyle = k.live === 'pre' ? T.candleLine : T.live;
        const yh = Y(k.h);
        g.fillRect(X(i) - (lw >> 1), yh, lw, Math.max(lw, Y(k.l) - yh));
      }
    } else {
      let bw = Math.max(lw + 2, Math.floor(this.barW * 0.7 * dpr));
      if ((bw - lw) % 2) bw -= 1;                 // match wick parity -> exact centring
      const geo = [];
      // pass 1: wicks + body outlines - one colour (theme/user wick colour) or each candle's own (wick 'match')
      for (let i = i0; i <= i1; i++) {
        const k = this.bars[i], cx = X(i), yh = Y(k.h);
        const top = Y(Math.max(k.o, k.c)), h = Math.max(lw, Y(Math.min(k.o, k.c)) - top);
        const left = cx - (bw >> 1);
        g.fillStyle = k.live ? (k.live === 'pre' ? T.candleLine : T.live)   // live candle: purple wick + outline
          : T.wick === 'match' ? (k.c >= k.o ? T.up : T.down) : T.candleLine;
        g.fillRect(cx - (lw >> 1), yh, lw, Math.max(lw, Y(k.l) - yh));
        g.fillRect(left, top, bw, h);
        geo.push([k.live ? null : k.c >= k.o, left, top, h, k.live, k.c >= k.o]);
      }
      // pass 2: fills. Outline is thinner than the wick so narrow bodies keep their colour;
      // when even that leaves no room (or the body is very short) the whole body is coloured.
      const bd = Math.max(1, Math.round(dpr * 0.75));
      // Body style (Settings -> Appearance): hollow = chart background inside the outline,
      // hollow_up = up candles hollow, down candles solid.
      for (const up of [true, false]) {
        const hollow = T.body === 'hollow' || (T.body === 'hollow_up' && up);
        g.fillStyle = hollow ? T.bg : up ? T.up : T.down;
        for (const [isUp, left, top, h] of geo) {
          if (isUp !== up) continue;
          if (hollow && T.wick !== 'match' && !(bw - 2 * bd >= lw && h > 2 * bd)) continue;   // too thin to hollow: keep outline
          const roomy = bw - 2 * bd >= lw && h > 2 * bd;
          if (roomy) g.fillRect(left + bd, top + bd, bw - 2 * bd, h - 2 * bd);
          else if (bw - 2 * bd < lw) g.fillRect(left, top, bw, h);
        }
      }
      // Today's unconfirmed candle (Chart2 live data), purple until the EOD bar arrives (pre-market: black).
      // Hollow (chart background) when up, solid when down, so its direction still reads.
      for (const [, left, top, h, live, up] of geo) {
        if (!live) continue;
        const roomy = bw - 2 * bd >= lw && h > 2 * bd;
        g.fillStyle = up ? T.bg : live === 'pre' ? T.candleLine : T.live;
        if (roomy) g.fillRect(left + bd, top + bd, bw - 2 * bd, h - 2 * bd);
      }
    }
    g.restore();

    // On-price indicators, clipped to the price pane.
    g.save();
    g.beginPath(); g.rect(0, 0, plotR, this.priceBottom + 1); g.clip();
    for (const s of this.overlays()) this.drawStudy(g, s, v => this.yOf(v, r), i0);
    g.restore();

    // Auto chart patterns + analyst rating dates, clipped to the price pane.
    g.save();
    g.beginPath(); g.rect(0, 0, plotR, this.priceBottom + 1); g.clip();
    this.drawPatterns(g, r);
    this.drawAnalyst(g, r);
    g.restore();

    // Indicator panes
    const panes = this.panes();
    for (const p of panes) this.drawPane(g, p, i0);
    const lay = panes.map(p => ({ key: p.s.key, top: Math.round(p.top), h: Math.round(p.h) }));
    const layKey = JSON.stringify(lay);
    if (layKey !== this._layKey) { this._layKey = layKey; this.onLayout(lay); }

    // Drawings sit above the candles, clipped to the price pane.
    if (this.editor) {
      g.save();
      g.beginPath(); g.rect(0, 0, plotR, this.priceBottom + 1); g.clip();
      this.editor.draw(g, r);
      g.restore();
      if (this.hasVol) {
        g.save();
        g.beginPath(); g.rect(0, this.volTop, plotR, this.mainBottom - this.volTop); g.clip();
        this.editor.draw(g, undefined, 'volume');
        g.restore();
      }
      for (const pane of panes) {
        g.save();
        g.beginPath(); g.rect(0, pane.top + 1, plotR, pane.h - 1); g.clip();
        this.editor.draw(g, undefined, pane.s.key);
        g.restore();
      }
    }

    // Alert rays: one per recorded trigger; numbers are chronological within the ticker/day.
    const labelYs = [];
    for (const mark of this.intraday ? [] : this.auditMarks) {   // daily-dated: not on intraday
      const di = this.dailyIdx(mark.d);
      if (di < 0 || this.daily[di].d !== mark.d) continue;
      const x = this.xOf(this.dispOfDaily[di]);
      const price = mark.price * (this.daily[di].f || 1);
      const y = this.yOf(price, r);
      if (x >= plotR || !Number.isFinite(y) || y < PAD_TOP || y > this.priceBottom) continue;
      const col = mark.direction === 'down' ? T.down : T.up;
      g.save();
      g.strokeStyle = col; g.fillStyle = col; g.lineWidth = 1.5;
      g.globalAlpha = 0.5;
      g.setLineDash([7, 4]); g.beginPath();
      g.moveTo(Math.max(0, x), Math.round(y) + 0.5);
      g.lineTo(plotR, Math.round(y) + 0.5); g.stroke();
      g.globalAlpha = 1; g.setLineDash([]);
      g.beginPath(); g.arc(x, y, 3.5, 0, Math.PI * 2); g.fill();
      const label = '#' + mark.number + (mark.direction === 'down' ? ' ▼' : ' ▲');
      g.font = 'bold 11px sans-serif'; g.textBaseline = 'middle'; g.textAlign = 'left';
      const labelX = Math.max(4, Math.min(x + 7, plotR - g.measureText(label).width - 4));
      let labelY = Math.max(PAD_TOP + 9, Math.min(y - 12, this.priceBottom - 9));
      while (labelYs.some(v => Math.abs(v - labelY) < 15) && labelY < this.priceBottom - 18) labelY += 15;
      labelYs.push(labelY);
      g.fillText(label, labelX, labelY);
      if (this.auditMarks.length === 1) this.axisTag(g, plotR, y, price.toFixed(dp), col);
      g.restore();
    }

    // Last close: dashed line across the plot + coloured tag on the axis.
    const last = this.bars[n - 1], ly = this.yOf(last.c, r);
    if (this.showLast && ly >= PAD_TOP && ly <= this.priceBottom) {
      const col = last.live === 'reg' ? T.live : last.live === 'pre' ? T.candleLine : last.c >= last.o ? T.up : T.down;
      g.strokeStyle = col; g.setLineDash([3, 3]); g.beginPath();
      g.moveTo(0, Math.round(ly) + 0.5); g.lineTo(plotR, Math.round(ly) + 0.5); g.stroke(); g.setLineDash([]);
      this.axisTag(g, plotR, ly, last.c.toFixed(dp), col);
    }

    // After-hours price (US, while today's candle is still unconfirmed): short dashed line + grey tag.
    if (this.postMark && last.live) {
      const py = this.yOf(this.postMark.p, r);
      if (py >= PAD_TOP && py <= this.priceBottom) {
        const x0 = this.xOf(n - 1) - this.barW;
        g.strokeStyle = T.muted; g.setLineDash([2, 2]); g.beginPath();
        g.moveTo(x0, Math.round(py) + 0.5); g.lineTo(plotR, Math.round(py) + 0.5); g.stroke(); g.setLineDash([]);
        this.axisTag(g, plotR, py, this.postMark.p.toFixed(dp), T.muted);
        g.fillStyle = T.muted; g.textAlign = 'right'; g.textBaseline = 'bottom';
        g.fillText('after hours', plotR - 4, Math.round(py) - 2);
      }
    }

    // Axis borders
    g.strokeStyle = T.grid; g.beginPath();
    g.moveTo(plotR + 0.5, 0); g.lineTo(plotR + 0.5, this.h);
    g.moveTo(0, this.h - AXIS_H + 0.5); g.lineTo(this.w, this.h - AXIS_H + 0.5);
    g.stroke();
    this._dp = dp;
  }

  // Candle index of a timestamp (the candle at or before it).
  idxOfTs(t) {
    const B = this.bars;
    let lo = 0, hi = B.length - 1;
    while (lo < hi) { const m = (lo + hi + 1) >> 1; if (B[m].t <= t) lo = m; else hi = m - 1; }
    return lo;
  }

  // Auto patterns: the two boundary lines from start to breakout, dashed while still
  // developing, green / red by direction (grey once failed), with a small name tag.
  drawPatterns(g, r) {
    if (!this.patterns.length) return;
    const val = (line, i) => line.a + line.b * i;
    const [i0, i1] = this.visible();
    let shown = 0;
    g.font = "10px 'JetBrains Mono', monospace"; g.textBaseline = 'middle';
    for (const p of this.patterns) {
      const xs = this.idxOfTs(p.startTs), xb = this.idxOfTs(p.breakoutTs);
      if (xs > i1 || xb < i0) continue;
      if (++shown > 48) break;
      const bear = p.direction === 'down' || p.id.startsWith('bear') || ['risingWedge', 'doubleTop', 'headShoulders'].includes(p.id);
      const col = p.stage === 'failed' ? '#8b8b8b' : bear ? '#c0392b' : '#18856b';
      g.save(); g.strokeStyle = col; g.lineWidth = 1.5; g.globalAlpha = 0.9;
      if (p.stage === 'developing') g.setLineDash([5, 4]);
      g.beginPath();
      for (const line of [p.upper, p.lower]) {
        g.moveTo(this.xOf(xs), this.yOf(val(line, p.start), r));
        g.lineTo(this.xOf(xb), this.yOf(val(line, p.breakout), r));
      }
      g.stroke(); g.restore();
      const label = `${p.name}${p.stage === 'confirmed' ? ' · breakout ' + p.direction : ' · ' + p.stage}`;
      const w = g.measureText(label).width + 8, x = Math.max(2, Math.min(this.xOf(this.idxOfTs(p.endTs)), this.plotR - w - 2));
      const y = Math.max(PAD_TOP + 8, this.yOf(val(p.upper, p.end), r) - 12);
      g.fillStyle = 'rgba(255,255,255,0.88)'; g.fillRect(x, y - 8, w, 16);
      g.strokeStyle = col; g.lineWidth = 1; g.strokeRect(x + 0.5, y - 7.5, w - 1, 15);
      g.fillStyle = col; g.textAlign = 'left'; g.fillText(label, x + 4, y);
    }
  }

  // Analyst rating dates: a faint vertical line on the rating's candle, coloured by rating,
  // with a dot at the price target (split-adjusted from that day's price basis). Look from analystStyle.
  analystX(m) { return this.xOf(this.ptIndex({ d: m.d, off: 0 })); }

  drawAnalyst(g, r) {
    if (this.intraday || !this.analystMarks.length || !this.daily?.length) return;
    const st = this.analystStyle;
    const dash = { solid: [], dashed: [6, 4], dotted: [2, 3] }[st.lineStyle] || [2, 3];
    for (const m of this.analystMarks) {
      const x = Math.round(this.analystX(m)) + 0.5;
      if (x < 0 || x > this.plotR) continue;
      if (st.line) {
        g.save(); g.strokeStyle = m.colour; g.globalAlpha = st.lineAlpha; g.setLineDash(dash); g.lineWidth = st.lineWidth;
        g.beginPath(); g.moveTo(x, PAD_TOP); g.lineTo(x, this.priceBottom); g.stroke(); g.restore();
      }
      const y = this.yOf(m.target * this.ptFactor({ d: m.d }), r);
      if (y >= PAD_TOP && y <= this.priceBottom) {
        g.save(); g.fillStyle = m.colour; g.globalAlpha = st.dotAlpha; g.beginPath(); g.arc(x, y, st.dotSize, 0, Math.PI * 2); g.fill(); g.restore();
      }
    }
  }

  // Range of an indicator pane: fixed (e.g. RSI 0-100) or fitted to what's visible.
  paneRange(s, i0) {
    if (s.key === 'volume') return this.volumeRange();
    const def = INDICATORS[s.id];
    if (def.range) return { min: def.range[0], max: def.range[1] };
    let lo = Infinity, hi = -Infinity;
    const i1 = Math.ceil(this.right);
    for (const o of def.outputs) {
      const a = s.out?.[o.k] || [];
      for (let i = i0; i <= i1 && i < a.length; i++) { const v = a[i]; if (v < lo) lo = v; if (v > hi) hi = v; }
      if (o.kind === 'hist') { lo = Math.min(lo, 0); hi = Math.max(hi, 0); }
    }
    for (const l of def.levels || []) { lo = Math.min(lo, l); hi = Math.max(hi, l); }
    if (!isFinite(lo)) return { min: 0, max: 1 };
    const pad = (hi - lo || Math.abs(hi) || 1) * 0.08;
    return { min: lo - pad, max: hi + pad };
  }

  drawPane(g, p, i0) {
    const T = this.theme, plotR = this.plotR, def = INDICATORS[p.s.id];
    const top = p.top + 4, bot = p.top + p.h - 4, r = this.paneRange(p.s, i0);
    const Y = v => top + (r.max - v) / (r.max - r.min || 1) * (bot - top);
    p.Y = Y; p.r = r;
    g.strokeStyle = T.grid; g.lineWidth = 1; g.beginPath();
    g.moveTo(0, Math.round(p.top) + 0.5); g.lineTo(this.w, Math.round(p.top) + 0.5); g.stroke();
    // Axis labels: the levels if it has any, else top / middle / bottom of the range.
    const ticks = def.levels?.length ? def.levels : [r.max - (r.max - r.min) * 0.1, (r.max + r.min) / 2, r.min + (r.max - r.min) * 0.1];
    const dp = Math.abs(r.max - r.min) >= 50 ? 0 : Math.abs(r.max - r.min) >= 1 ? 2 : 4;
    g.font = T.font; g.fillStyle = T.muted; g.textAlign = 'left'; g.textBaseline = 'middle';
    for (const t of ticks) g.fillText(fmtNum(t, dp), plotR + 6, Y(t));
    if (def.levels?.length) {
      g.save(); g.strokeStyle = T.muted; g.globalAlpha = 0.5; g.setLineDash([4, 4]); g.beginPath();
      for (const l of def.levels) { const y = Math.round(Y(l)) + 0.5; g.moveTo(0, y); g.lineTo(plotR, y); }
      g.stroke(); g.restore();
    }
    g.save(); g.beginPath(); g.rect(0, p.top + 1, plotR, p.h - 1); g.clip();
    this.drawStudy(g, p.s, Y, i0);
    g.restore();
  }

  // Lines, histogram bars, dots and band fills of one indicator.
  drawStudy(g, s, Y, i0) {
    const def = INDICATORS[s.id], T = this.theme, out = s.out || {}, j0 = Math.max(0, i0 - 1), j1 = Math.ceil(this.right) + 1;
    for (const [ka, kb, col, col2] of def.fills || []) {
      const a = out[ka], b = out[kb];
      if (!a || !b) continue;
      g.save();
      for (let i = j0; i < Math.min(j1, a.length - 1); i++) {
        if ([a[i], b[i], a[i + 1], b[i + 1]].some(Number.isNaN)) continue;
        const c = col2 && a[i] < b[i] ? col2 : col;
        g.fillStyle = hexAlpha(c, s.fillOpacity ?? 0.08);
        const x1 = this.xOf(i), x2 = this.xOf(i + 1);
        g.beginPath(); g.moveTo(x1, Y(a[i])); g.lineTo(x2, Y(a[i + 1])); g.lineTo(x2, Y(b[i + 1])); g.lineTo(x1, Y(b[i])); g.closePath(); g.fill();
      }
      g.restore();
    }
    for (const o of def.outputs) {
      const a = out[o.k], st = s.styles?.[o.k] || { color: o.color, width: 1.5 };
      if (!a || st.hidden) continue;
      if (o.kind === 'hist') {
        const bw = Math.max(1, this.barW * 0.6), y0 = Y(0);
        for (let i = j0; i < Math.min(j1, a.length); i++) {
          if (Number.isNaN(a[i])) continue;
          g.fillStyle = hexAlpha(a[i] >= 0 ? T.up : T.down, 0.55);
          const y = Y(a[i]);
          g.fillRect(this.xOf(i) - bw / 2, Math.min(y, y0), bw, Math.max(1, Math.abs(y - y0)));
        }
      } else if (o.kind === 'dots') {
        const rad = clamp(this.barW * 0.2, 1.2, 3);
        g.fillStyle = st.color;
        for (let i = j0; i < Math.min(j1, a.length); i++) {
          if (Number.isNaN(a[i])) continue;
          g.beginPath(); g.arc(this.xOf(i), Y(a[i]), rad, 0, Math.PI * 2); g.fill();
        }
      } else {
        g.strokeStyle = st.color; g.lineWidth = st.width; g.beginPath();
        let pen = false;
        for (let i = j0; i < Math.min(j1, a.length); i++) {
          if (Number.isNaN(a[i])) { pen = false; continue; }
          const x = this.xOf(i), y = Y(a[i]);
          if (pen) g.lineTo(x, y); else { g.moveTo(x, y); pen = true; }
        }
        g.stroke();
      }
    }
  }

  axisTag(g, x, y, text, bg) {
    g.fillStyle = bg;
    g.fillRect(x + 1, y - 9, AXIS_W - 1, 18);
    g.fillStyle = bg === this.theme.accent ? this.theme.onAccent : '#fff'; g.textAlign = 'left'; g.textBaseline = 'middle';
    g.fillText(text, x + 6, y);
  }

  // ── Overlay layer: crosshair ──────────────────────────────────────────────
  drawOver() {
    if (!this.w) return;
    const g = this.ctx(this.over), T = this.theme, n = this.bars.length;
    g.clearRect(0, 0, this.w, this.h);
    const emit = (b, idx = null) => { if (b !== this._hov || idx !== this._hovIdx) { this._hov = b; this._hovIdx = idx; this.onHover(b, idx); } };
    if (!this.cross || !n) return emit(null);
    const { y } = this.cross, plotR = this.plotR;
    const idx = Math.round(this.idxAt(this.cross.x));
    const x = Math.round(this.xOf(idx)) + 0.5;
    if (x < 0 || x > plotR) return emit(null);

    g.strokeStyle = T.muted; g.lineWidth = 1; g.setLineDash([4, 3]); g.beginPath();
    g.moveTo(x, 0); g.lineTo(x, this.h - AXIS_H);
    if (y < this.h - AXIS_H) { g.moveTo(0, Math.round(y) + 0.5); g.lineTo(plotR, Math.round(y) + 0.5); }
    g.stroke(); g.setLineDash([]);
    g.font = T.font;

    if (y >= PAD_TOP && y <= this.priceBottom) this.axisTag(g, plotR, y, this.priceAt(y, this.yRange()).toFixed(this._dp || 2), T.accent);
    const am = this.analystMarks.find(m => Math.abs(this.analystX(m) - this.cross.x) <= 4);
    if (am && y <= this.priceBottom) {          // analyst rating under the crosshair
      const text = `${am.firm} · ${am.rating} · target ${am.target.toFixed(2)} · ${am.d}`;
      g.font = T.font;
      const tw = g.measureText(text).width + 10, tx = clamp(this.analystX(am) + 6, 2, plotR - tw - 2);
      g.fillStyle = am.colour; g.fillRect(tx, PAD_TOP + 2, tw, 18);
      g.fillStyle = '#fff'; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(text, tx + 5, PAD_TOP + 11);
    }
    for (const p of this.panes()) {             // value tag inside an indicator pane
      if (y <= p.top || y >= p.top + p.h) continue;
      const r = this.paneRange(p.s, this.visible()[0]), top = p.top + 4, bot = p.top + p.h - 4;
      const v = r.max - (y - top) / (bot - top) * (r.max - r.min);
      this.axisTag(g, plotR, y, fmtNum(v, Math.abs(r.max - r.min) >= 50 ? 0 : 2), T.accent);
    }

    // Date tag; blank slots after the last candle show their distance as +N candles.
    const bar = idx >= 0 && idx < n ? this.bars[idx] : null;
    const label = bar ? (this.intraday ? `${bar.wd} ${bar.dd} ${MONTHS[bar.mo - 1]} ${hhmm(bar.min)}`
                                       : new Date(bar.t).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' }))
                      : idx >= n ? `+${idx - n + 1}` : '';
    if (label) {
      const tw = g.measureText(label).width + 12;
      g.fillStyle = T.accent;
      g.fillRect(clamp(x - tw / 2, 0, plotR - tw), this.h - AXIS_H + 1, tw, AXIS_H - 1);
      g.fillStyle = T.onAccent; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText(label, clamp(x, tw / 2, plotR - tw / 2), this.h - AXIS_H / 2);
    }
    emit(bar, idx);
  }

  // ── Magnifier: copies of both layers around `loupe`, zoomed. Top-right of the plot,
  // moving to top-left while the finger is under that corner.
  drawLens() {
    const L = this.lens, p = this.loupe;
    if (!p || !this.w) { L.style.display = 'none'; return; }
    const d = this.dpr, px = Math.round(LENS * d);
    if (L.width !== px) L.width = L.height = px;
    const under = p.x > this.plotR - LENS - 40 && p.y < LENS + 40;
    Object.assign(L.style, { display: 'block', left: under ? '6px' : '', right: under ? '' : (AXIS_W + 6) + 'px' });
    const g = L.getContext('2d'), sw = px / LENS_ZOOM;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.fillStyle = this.theme.bg; g.fillRect(0, 0, px, px);
    for (const c of [this.base, this.over]) g.drawImage(c, p.x * d - sw / 2, p.y * d - sw / 2, sw, sw, 0, 0, px, px);
    const m = px / 2;                            // exact point: small ring + ticks
    g.strokeStyle = this.theme.accent; g.lineWidth = d * 1.5; g.beginPath();
    g.arc(m, m, 4 * d, 0, Math.PI * 2);
    for (const [x1, y1, x2, y2] of [[m - 14 * d, m, m - 7 * d, m], [m + 7 * d, m, m + 14 * d, m], [m, m - 14 * d, m, m - 7 * d], [m, m + 7 * d, m, m + 14 * d]]) { g.moveTo(x1, y1); g.lineTo(x2, y2); }
    g.stroke();
  }
}

function fmtNum(v, dp) {
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(1) + 'B';
  if (a >= 1e6) return (v / 1e6).toFixed(1) + 'M';
  if (a >= 1e4) return (v / 1e3).toFixed(1) + 'K';
  return v.toFixed(dp);
}

function hexAlpha(hex, a) {
  const h = (hex || '#000').replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
